import type { ComponentType, SVGProps } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { CalendarPlus, ChatBubble, Community, PageEdit as PageEditIcon, UploadSquare } from 'iconoir-react'
import { startCase } from 'lodash'
import moment from 'moment'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import PhoneNumber from '../PhoneNumber'
import Spinner from '../Spinner'
import SimpleTable from '../table/SimpleTable'
import { ImportWizardContext, ImportFile } from 'app/components/ImportWizard'
import { showModal } from 'app/lib/modal'
import { useUser } from 'app/utils'
import { isSMCOrg } from 'lib/isSMCOrg'
import { logError } from 'lib/logging'

const emptyCellClassName = `bg-red-300 hover:bg-red-300 text-body-xs text-red-200 !p-0 after:content-['-'] after:pl-2`
type MissingField = {
  [key in `rowNumber` | `value` | `missingField`]: string | number | boolean | Date | undefined
}

export default function Step4() {
  const user = useUser()
  const isSMC = user ? isSMCOrg(user.orgId) : false
  const {
    csvFile,
    columnMapping,
    locationWorkspaceMapping,
    errors,
    openErrorModal,
    dataToImport,
    setErrors,
    setOpenErrorModal,
    setDataToImport,
  } = useContext(ImportWizardContext)
  let [isLoading, setIsLoading] = useState<boolean>(true)
  let [missingFields, setMissingFields] = useState<MissingField[]>([])
  let missingFieldsStyle = (cell) => (cell ? `` : emptyCellClassName)

  useEffect(() => {
    let errorCells = document.getElementsByClassName(emptyCellClassName)
    if (errorCells.length > 0) {
      logError(`Step4: Error cells found:`, errorCells)
      setErrors(errorCells.length)
    }
  }, [dataToImport])

  useEffect(() => {
    let columnMap = columnMapping.reduce((acc, column) => ({ ...acc, [column.mapping as any]: column?.columnName }), {})

    setDataToImport(
      [...dataToImport].map((item: ImportFile, i) => {
        let workspaceColumnMapped = locationWorkspaceMapping.find(
          (workspace, mappingIndex) =>
            (item[columnMap.workspace] === workspace.sheetWorkspace ||
              item[columnMap.workspace] === workspace?.mappedWorkspace) &&
            i === mappingIndex,
        )
        let locationColumnMapped = locationWorkspaceMapping.find(
          (location, mappingIndex) =>
            (item[columnMap.location] === location.sheetLocation ||
              item[columnMap.location] === location?.mappedLocation) &&
            i === mappingIndex,
        )
        return {
          workspace: workspaceColumnMapped?.mappedWorkspace ?? ``,
          location: locationColumnMapped?.mappedLocation ?? ``,
          activityType: item[columnMap.activityType],
          requestedTreatment: item[columnMap.requestedTreatment],
          channel: item[columnMap.channel],
          vendor: item[columnMap.vendor],
          name: item[columnMap.name],
          activityDate: item[columnMap.activityDate],
          requestedAppointmentDate: item[columnMap.requestedAppointmentDate],
          phone: item[columnMap.phone],
          leadOwner: item[columnMap.contactOwner] ?? item[columnMap.leadOwner],
          email: item[columnMap.email],
          scheduled: item[columnMap.scheduled],
          utmSource: item[columnMap.utmSource],
          utmMedium: item[columnMap.utmMedium],
          utmCampaign: item[columnMap.utmCampaign],
          workspaceId: workspaceColumnMapped?.workspaceId,
          locationId: locationColumnMapped?.locationId,
          locationName: locationColumnMapped?.mappedLocation ?? ``,
        } as ImportFile
      }),
    )
    setIsLoading(false)
  }, [])

  useEffect(() => {
    let missingFields: MissingField[] = []
    if (dataToImport.length === 0) return
    dataToImport.forEach((item: ImportFile, i: number) => {
      Object.entries(item).forEach(([key, value]) => {
        //optional values not required to be checked. Workspace and locations ids will be completed at import time
        let optionalFields = [
          `workspaceId`,
          `locationId`,
          `locationName`,
          `email`,
          `leadOwner`,
          `requestedTreatment`,
          `lineNumber`,
          `requestedAppointmentDate`,
          `vendor`,
          `utmSource`,
          `utmMedium`,
          `utmCampaign`,
        ]

        // For non-SMC organizations, channel is not required (they use UTM fields instead)
        // UTM fields are never required for any organization type
        if (!isSMC) {
          optionalFields.push(`channel`)
        }

        if (optionalFields.includes(key)) return
        // A required field might have 'Unknown' as value, so we need to check for that
        if (
          !value ||
          ([`activityDate`].includes(key) &&
            (moment(value, `M/D/YYYY`, true).isValid() === false || (value as string).toLowerCase() === `unknown`))
        ) {
          missingFields = [...missingFields].concat({ rowNumber: i + 1, value, missingField: startCase(key) })
        }
      })
    })
    setMissingFields(missingFields)
  }, [dataToImport])

  let previewColumns = useMemo(
    () => [
      {
        id: `workspace`,
        header: `Workspace`,
        accessorFn: (row) => row.workspace,
        meta: {
          getCellStyle: missingFieldsStyle,
          defaultVisible: true,
        },
      },
      {
        id: `location`,
        header: `Location`,
        accessorFn: (row) => row.location,
        meta: {
          getCellStyle: missingFieldsStyle,
          defaultVisible: true,
        },
      },
      {
        id: `Vendor`,
        header: `Vendor`,
        accessorFn: (row) => row.vendor,
        meta: {
          defaultVisible: true,
        },
      },
      {
        id: `Activity Type`,
        header: `Activity Type`,
        accessorFn: (row) => row.activityType,
        cell: (props) => {
          let Icon: ComponentType<SVGProps<SVGSVGElement>>
          switch (props.getValue()) {
            case `Chat`:
              Icon = ChatBubble
              break
            case `Form`:
              Icon = PageEditIcon
              break
            case `Online Scheduler`:
              Icon = CalendarPlus
              break
            case `Virtual Consultation`:
              Icon = Community
              break
            default:
              Icon = UploadSquare
              break
          }
          return (
            <div className="flex gap-x-2 align-middle">
              <Icon className="h-4 w-4 shrink-0" />
              {props.getValue()}
            </div>
          )
        },
        meta: {
          defaultVisible: true,
          getCellStyle: missingFieldsStyle,
        },
      },
      {
        id: `name`,
        header: `Name`,
        accessorFn: (row) => row.name,
        meta: {
          defaultVisible: true,
          getCellStyle: missingFieldsStyle,
        },
      },
      {
        id: `Activity Date`,
        header: `Activity Date`,
        accessorFn: (row) =>
          moment(row.activityDate, `M/D/YYYY`, true).isValid() ||
          (row.activityDate as string).toLowerCase() === `unknown`
            ? row.activityDate
            : null,
        cell: (props) => props.getValue(),
        meta: {
          defaultVisible: true,
          getCellStyle: missingFieldsStyle,
        },
      },
      {
        id: `Requested Appointment Date`,
        header: `Requested Appointment Date`,
        accessorFn: (row) => row.requestedAppointmentDate,
        cell: (props) => props.getValue(),
        meta: {
          defaultVisible: true,
        },
      },
      {
        id: `phone`,
        header: `Phone`,
        accessorFn: (row) => row.phone,
        cell: (props) =>
          props.getValue().toLowerCase() === `unknown` ? props.getValue() : <PhoneNumber number={props.getValue()} />,
        meta: {
          defaultVisible: true,
          getCellStyle: missingFieldsStyle,
        },
      },
      {
        id: `email`,
        header: `Email`,
        accessorFn: (row) => row.email,
        meta: {
          defaultVisible: true,
        },
      },
      {
        id: `scheduled`,
        header: `Scheduled?`,
        accessorFn: (row) => row.scheduled,
        meta: {
          defaultVisible: true,
          getCellStyle: missingFieldsStyle,
        },
      },
      {
        id: `requestedTreatment`,
        header: `Requested Treatment`,
        accessorFn: (row) => row.requestedTreatment,
        meta: {
          defaultVisible: true,
        },
      },
      ...(isSMC
        ? [
            {
              id: `channel`,
              header: `Channel`,
              accessorFn: (row) => row.channel,
              meta: {
                defaultVisible: true,
                getCellStyle: missingFieldsStyle,
              },
            },
          ]
        : [
            {
              id: `utmSource`,
              header: `Source`,
              accessorFn: (row) => row.utmSource,
              meta: {
                defaultVisible: true,
              },
            },
            {
              id: `utmMedium`,
              header: `Medium`,
              accessorFn: (row) => row.utmMedium,
              meta: {
                defaultVisible: true,
              },
            },
            {
              id: `utmCampaign`,
              header: `Campaign`,
              accessorFn: (row) => row.utmCampaign,
              meta: {
                defaultVisible: true,
              },
            },
          ]),
      {
        id: `Contact Owner`,
        header: `Contact Owner`,
        accessorFn: (row) => row.leadOwner ?? row.contactOwner,
        meta: {
          defaultVisible: true,
        },
      },
    ],
    [csvFile],
  )

  let errorColumns = useMemo(
    (): ColumnDef<MissingField, any>[] => [
      {
        id: `rowNumber`,
        header: `Row Number`,
        accessorFn: (row) => row.rowNumber,
        meta: {
          defaultVisible: true,
        },
      },
      {
        id: `value`,
        header: `Value`,
        accessorFn: (row) => row.value,
        meta: {
          defaultVisible: true,
        },
      },
      {
        id: `missingField`,
        header: `Missing Field`,
        accessorFn: (row) => row.missingField,
        meta: {
          defaultVisible: true,
        },
      },
    ],
    [errors],
  )

  useEffect(() => {
    if (openErrorModal)
      showModal(<SimpleTable<MissingField> columns={errorColumns} data={missingFields} hideFooter />, {
        title: `Errors found`,
        isFullScreen: false,
        is80Percent: true,
        onClose: () => setOpenErrorModal(false),
        useRadix: true,
      })
  }, [openErrorModal])

  if (!user) return null

  return (
    <>
      <h5 className="mb-4 text-strong">Preview of the Import</h5>
      {isLoading ? (
        <div className="flex justify-center">
          <Spinner size={20} />
        </div>
      ) : (
        <>
          {errors > 0 && (
            <span className="mb-8 mt-3 text-body-sm capitalize text-red-500">{`Errors found: ${errors}`}</span>
          )}
          <SimpleTable columns={previewColumns} data={dataToImport} hideFooter />
        </>
      )}
    </>
  )
}
